import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Paper,
  Button,
  Grid,
  Chip,
  Avatar,
  Modal,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import Sidebar from "../sideBar";
import { allRejectedTutors } from "../../../state/slices/admin";
import { useSelector, useDispatch } from "react-redux";
import { GridOverlay } from "@mui/x-data-grid";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import { alpha } from "@mui/material";
import Loading from "../../../components/loading/loading";
import TutorDetailPage from "../TutordetailPage";

const RejectedTutorPage = () => {
  const theme = useMuiTheme();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedTutor, setSelectedTutor] = useState(null);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const dispatch = useDispatch();
  const {rejectedTutors , loading} = useSelector((state) => state.admin);
  useEffect(() => {
    dispatch(allRejectedTutors());
  }, [dispatch]);

  const columns = [
    { field: "user", headerName: t('dashboard.user'), width: 150 },
    { field: "subject", headerName: t('dashboard.subject'), width: 150 },
    { field: "department", headerName: t('dashboard.department'), width: 150 },
    { field: "University_Name", headerName: t('dashboard.universityName'), width: 180 },
    { field: "year", headerName: t('dashboard.year'), width: 100 },
    { field: "degreeType", headerName: t('dashboard.degreeType'), width: 120 },
    { field: "degreeYear", headerName: t('dashboard.degreeYear'), width: 120 },
    { field: "graduated", headerName: t('dashboard.graduated'), width: 100 },
    { field: "status", headerName: t('dashboard.status'), width: 120 },
    { field: "registrationDate", headerName: t('dashboard.registrationDate'), width: 170 },
    { field: "location", headerName: t('dashboard.location'), width: 150 },
    { field: "tutoringType", headerName: t('dashboard.tutoringType'), width: 150 },
    { field: "experience", headerName: t('dashboard.experience'), width: 150 },
    { field: "availability", headerName: t('dashboard.availability'), width: 150 },
    { field: "rate", headerName: t('dashboard.rate'), width: 120 },
  ];

  const rows = rejectedTutors.map((tutor) => {
    // Handle subjects - can be array of strings or objects
    const subjectsDisplay = tutor.subjects && tutor.subjects.length > 0
      ? (typeof tutor.subjects[0] === 'string'
          ? tutor.subjects.join(', ')
          : tutor.subjects.map(s => s.subject || s).filter(Boolean).join(', '))
      : 'N/A';
    
    // Handle tutoringType - can be array
    const tutoringTypeDisplay = tutor.tutoringType && tutor.tutoringType.length > 0
      ? tutor.tutoringType.map(type => type === "IN_PERSON" ? t('common.inPerson') : t('common.online')).join(', ')
      : 'N/A';
    
    return {
      id: tutor._id,
      user: tutor.userId ? `${tutor.userId.firstName || ''} ${tutor.userId.lastName || ''}`.trim() : 'N/A',
      subject: subjectsDisplay,
      department: tutor.education?.department || 'N/A',
      University_Name: tutor.education?.universityName || 'N/A',
      year: tutor.education?.year || 'N/A',
      degreeType: tutor.education?.degreeType || 'N/A',
      degreeYear: tutor.education?.degreeYear || 'N/A',
      graduated: tutor.education?.graduated ? t('dashboard.yes') : t('dashboard.no'),
      status: tutor.approved || 'N/A',
      registrationDate: tutor.userId?.createdAt
        ? new Date(tutor.userId.createdAt).toLocaleDateString()
        : 'N/A',
      location: tutor.location || 'N/A',
      tutoringType: tutoringTypeDisplay,
      experience: tutor.experience || 'N/A',
      availability: tutor.availability || 'N/A',
      rate: tutor.rate || 'N/A',
    };
  });


  const CustomNoRowsOverlay = () => (
    <GridOverlay>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          color: "secondary",
        }}
      >
        <Typography variant="h6">{t('dashboard.noRejectedTutors')}</Typography>
      </Box>
    </GridOverlay>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: alpha(theme.palette.primary.main, 0.03) }}>
      <Grid container>
        <Grid item xs={12} md={2}>
          <Sidebar />
        </Grid>
        <Grid item xs={12} md={10}>
          <Box sx={{ p: { xs: 2, md: 4 } }}>
            <Typography 
              variant="h4" 
              sx={{ 
                fontWeight: 700, 
                color: theme.palette.primary.main, 
                mb: 3 
              }}
            >
              {t('dashboard.tutorDetails')} - {t('request.rejected')}
            </Typography>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3,
                borderRadius: 3,
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <div style={{ height: 600, width: "100%" }}>
                {loading ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <Loading />
                  </Box>
                ) : (
                  <DataGrid
                    columns={columns}
                    rows={rows}
                    columnHeaderHeight={60}
                    onCellClick={(e) => {
                      const tutor = rejectedTutors.find(
                        (tutor) => tutor._id === e.row.id
                      );
                      setSelectedTutor(tutor);
                      handleOpen();
                    }}
                    components={{
                      NoRowsOverlay: CustomNoRowsOverlay,
                    }}
                    sx={{
                      '& .MuiDataGrid-cell:hover': {
                        cursor: 'pointer',
                      },
                      '& .MuiDataGrid-row:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  />
                )}
              </div>
            </Paper>
          </Box>
        </Grid>
        {selectedTutor && <TutorDetailPage open={open} handleClose={handleClose} data={selectedTutor} />}
      </Grid>
    </Box>
  );
};

export default RejectedTutorPage;
